import { PortfolioPosition, PortfolioSettings, PerformanceSnapshot } from '../types';
import { v4 as uuidv4 } from 'uuid';
import config from '../config';

// Mock data
let mockSettings: PortfolioSettings = {
  id: 'mock-settings-id',
  user_id: 'mock-user-id',
  initial_cash: 100000,
  current_cash: 85000,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString()
};

let mockPositions: PortfolioPosition[] = [
  {
    id: 'position-1',
    user_id: 'mock-user-id',
    ticker: 'AAPL',
    shares: 25,
    entry_price: 175.50,
    entry_date: '2023-03-15',
    is_active: true,
    created_at: '2023-03-15T12:00:00Z',
    updated_at: '2023-03-15T12:00:00Z'
  },
  {
    id: 'position-2',
    user_id: 'mock-user-id',
    ticker: 'MSFT',
    shares: 15,
    entry_price: 305.20,
    entry_date: '2023-04-20',
    is_active: true,
    created_at: '2023-04-20T14:30:00Z',
    updated_at: '2023-04-20T14:30:00Z'
  },
  {
    id: 'position-3',
    user_id: 'mock-user-id',
    ticker: 'GOOGL',
    shares: 10,
    entry_price: 122.80,
    entry_date: '2023-05-10',
    is_active: true,
    created_at: '2023-05-10T09:15:00Z',
    updated_at: '2023-05-10T09:15:00Z'
  }
];

let mockSnapshots: PerformanceSnapshot[] = Array.from({ length: 30 }, (_, i) => {
  const date = new Date();
  date.setDate(date.getDate() - (29 - i));
  
  // Generate some random value fluctuations
  const portfolioValue = 85000 + Math.floor(Math.random() * 10000) + (i * 150);
  const cashBalance = 85000 - (i * 100);
  
  return {
    id: `snapshot-${i}`,
    user_id: 'mock-user-id',
    date: date.toISOString().split('T')[0],
    portfolio_value: portfolioValue,
    cash_balance: cashBalance,
    created_at: date.toISOString()
  };
});

// Get mock historical data with performance trending upward
mockSnapshots = mockSnapshots.sort((a, b) => 
  new Date(a.date).getTime() - new Date(b.date).getTime()
);

/**
 * Mock Portfolio Management Service
 * This provides a simulation of a portfolio management system for development and testing
 */

/**
 * Get portfolio settings
 */
const getPortfolioSettings = async (): Promise<PortfolioSettings> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return { ...mockSettings };
};

/**
 * Update portfolio settings
 */
const updatePortfolioSettings = async (settings: Partial<PortfolioSettings>): Promise<PortfolioSettings> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  mockSettings = {
    ...mockSettings,
    ...settings,
    updated_at: new Date().toISOString()
  };
  
  return { ...mockSettings };
};

/**
 * Get portfolio positions
 */
const getPositions = async (activeOnly: boolean = true): Promise<PortfolioPosition[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return mockPositions
    .filter(position => !activeOnly || position.is_active)
    .map(position => ({ ...position }));
};

/**
 * Create a new position (buy)
 */
const createPosition = async (position: Partial<PortfolioPosition>): Promise<PortfolioPosition> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 700));
  
  // Calculate cost
  const shares = position.shares || 0;
  const price = position.entry_price || 0;
  const cost = shares * price;
  
  // Check if enough cash is available
  if (cost > mockSettings.current_cash) {
    throw new Error(`Insufficient funds. Available: $${mockSettings.current_cash.toFixed(2)}`);
  }
  
  // Create new position
  const newPosition: PortfolioPosition = {
    id: uuidv4(),
    user_id: 'mock-user-id',
    ticker: position.ticker || '',
    shares: shares,
    entry_price: price,
    entry_date: position.entry_date || new Date().toISOString().split('T')[0],
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
  
  // Update cash balance
  mockSettings.current_cash -= cost;
  mockSettings.updated_at = new Date().toISOString();
  
  // Add to positions
  mockPositions.push(newPosition);
  
  return { ...newPosition };
};

/**
 * Close a position (sell)
 */
const closePosition = async (
  positionId: string,
  exitPrice: number,
  exitDate: string
): Promise<PortfolioPosition> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 700));
  
  // Find the position
  const positionIndex = mockPositions.findIndex(p => p.id === positionId);
  
  if (positionIndex === -1) {
    throw new Error('Position not found');
  }
  
  const position = mockPositions[positionIndex];
  
  // Update position status
  const updatedPosition: PortfolioPosition = {
    ...position,
    is_active: false,
    exit_price: exitPrice,
    exit_date: exitDate,
    updated_at: new Date().toISOString()
  };
  
  // Update the position
  mockPositions[positionIndex] = updatedPosition;
  
  // Add proceeds to cash balance
  const proceeds = position.shares * exitPrice;
  mockSettings.current_cash += proceeds;
  mockSettings.updated_at = new Date().toISOString();
  
  return { ...updatedPosition };
};

/**
 * Take a performance snapshot
 */
const takeSnapshot = async (portfolioValue: number): Promise<PerformanceSnapshot> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const snapshot: PerformanceSnapshot = {
    id: uuidv4(),
    user_id: 'mock-user-id',
    date: new Date().toISOString().split('T')[0],
    portfolio_value: portfolioValue,
    cash_balance: mockSettings.current_cash,
    created_at: new Date().toISOString()
  };
  
  // Add to snapshots
  mockSnapshots.push(snapshot);
  
  return { ...snapshot };
};

/**
 * Get performance snapshots
 */
const getSnapshots = async (limit: number = 30): Promise<PerformanceSnapshot[]> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return [...mockSnapshots]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, limit);
};

/**
 * Reset portfolio to initial state
 */
const resetPortfolio = async (initialCash: number = 100000): Promise<PortfolioSettings> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Reset portfolio settings
  mockSettings = {
    ...mockSettings,
    initial_cash: initialCash,
    current_cash: initialCash,
    updated_at: new Date().toISOString()
  };
  
  // Close all positions
  mockPositions = mockPositions.map(position => ({
    ...position,
    is_active: false,
    exit_price: position.entry_price,
    exit_date: new Date().toISOString().split('T')[0],
    updated_at: new Date().toISOString()
  }));
  
  return { ...mockSettings };
};

// Mock portfolio service interface
const mockPortfolioService = {
  getSettings: getPortfolioSettings,
  updateSettings: updatePortfolioSettings,
  getPositions,
  createPosition,
  closePosition,
  takeSnapshot,
  getSnapshots,
  resetPortfolio
};

export default mockPortfolioService; 